import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  FindingDetail,
  CollectionRun,
  FindingDataSelection,
  FindingListQuery,
  FindingListResponse,
  RepositoryScope,
  AssessmentSelection,
  AssessmentPreview,
  AssessmentRun,
  HumanReviewInput,
} from '@internal/plugin-dependency-security-common';
import type { DependencySecurityApi } from './types';

export class DependencySecurityClient implements DependencySecurityApi {
  saveHumanReview(id: string, input: HumanReviewInput): Promise<void> {
    return this.request(`/findings/${encodeURIComponent(id)}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  revokeHumanReview(id: string, reviewId: string): Promise<void> {
    return this.request(
      `/findings/${encodeURIComponent(id)}/reviews/${encodeURIComponent(
        reviewId,
      )}`,
      { method: 'DELETE' },
    );
  }
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  async getFindings(
    query: FindingListQuery = {},
  ): Promise<FindingListResponse> {
    const search = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) {
      if (value !== undefined) search.set(name, String(value));
    }
    const suffix = search.size > 0 ? `?${search.toString()}` : '';
    return this.request(`/findings${suffix}`);
  }

  async getFinding(
    id: string,
    selection: FindingDataSelection = {},
  ): Promise<FindingDetail> {
    const suffix = selection.demo ? '?demo=true' : '';
    return this.request(`/findings/${encodeURIComponent(id)}${suffix}`);
  }

  async getRepositories(): Promise<RepositoryScope> {
    return this.request('/repositories');
  }

  async getLatestCollectionRun(): Promise<CollectionRun | null> {
    return this.request('/collection-runs/latest');
  }

  async startCollection(repository: string): Promise<CollectionRun> {
    const search = new URLSearchParams({ repository });
    return this.request(`/collection-runs?${search.toString()}`, {
      method: 'POST',
    });
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl(
      'dependency-security',
    );
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}${path}`,
      init,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json() as Promise<T>;
  }

  previewAssessment(
    selection: AssessmentSelection,
  ): Promise<AssessmentPreview> {
    return this.request(
      `/assessment-runs/preview?${assessmentQuery(selection)}`,
    );
  }

  startAssessment(selection: AssessmentSelection): Promise<AssessmentRun> {
    return this.request(`/assessment-runs?${assessmentQuery(selection)}`, {
      method: 'POST',
    });
  }

  getLatestAssessmentRun(
    selection: AssessmentSelection,
  ): Promise<AssessmentRun | null> {
    return this.request(
      `/assessment-runs/latest?${assessmentQuery(selection)}`,
    );
  }
}

function assessmentQuery(selection: AssessmentSelection): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(selection)) {
    if (value !== undefined) search.set(key, String(value));
  }
  return search.toString();
}
